import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router-dom";
import { FeedbackSummary } from "../../v2/insights/Summary/HintsAndErrorView";
import { ClassExtended, CourseStructureCtx } from "../../../interfaces";
import { useContext, useEffect, useState } from "react";
import { getClassesTargetFeedback, HandleDataLayer } from "../../../Helpers";
import {
  TargetFeedbackData,
  TargetFeedbackDataResponse,
  TargetFeedbackRelianceData,
} from "@backend-types";
import { CourseStructureContext } from "../../../context/CourseStructureContext";

const useStyles = makeStyles((theme) => ({
  lessonPlanModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "764px",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  button: {
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "19px",
  },
}));

interface LessonPlanModalProps {
  open: boolean;
  targetId: string | null;
  loadedData: FeedbackSummary | null;
  classList: ClassExtended[] | null;
  handleClose: () => void;
}

type ClassData = {
  class_id: number;
  period_block: string;
  subject: string;
  totalFeedbackError: number;
};

export const LessonPlanModal = (props: LessonPlanModalProps) => {
  const { open, handleClose, targetId, classList } = props;
  const classes = useStyles();
  const history = useHistory();
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentTarget = targetId
    ? courseStructureCtx.courseTreeStructure?.getTarget(targetId)
    : null;

  const targetSkills = currentTarget && currentTarget?.getAllTargetSkills();

  const [loadedDetailedData, setLoadedDetailedData] = useState(false);
  // const [granularData, setGranularData] = useState<TargetFeedbackDataResponse | null>(
  //   null
  // );
  const [highestQuestion, setHighestQuestion] = useState<TargetFeedbackData | null>(null);
  const [highestClass, setHighestClass] = useState<ClassData | null>(null);

  const handleClickClass = () => {
    if (!highestClass) return;
    history.push(`/insights/class/${highestClass?.class_id}/summary`);
  };

  const handleClickActivity = () => {
    if (!highestQuestion) return;
    history.push(`/macs-wrapper?questionId=${highestQuestion.questionId}`);
  };

  const findMaxFeedbackEntry = (
    response: TargetFeedbackDataResponse
  ): TargetFeedbackData | null => {
    if (!response || !response.tableData || response.tableData.length === 0) {
      return null;
    }

    return response.tableData.reduce((prev, current) => {
      const prevTotal = prev.feedbackHintTotal + prev.feedbackErrorTotal;
      const currentTotal = current.feedbackHintTotal + current.feedbackErrorTotal;

      return currentTotal > prevTotal ? current : prev;
    });
  };

  const findClassWithMaxError = (
    hintReliance: TargetFeedbackRelianceData[]
  ): ClassData => {
    let classDataMap: Map<number, ClassData> = new Map();

    hintReliance.forEach((student) => {
      let classData = classDataMap.get(student.class_id ?? -1);
      if (classData) {
        classData.totalFeedbackError += student.feedbackError;
      } else {
        classDataMap.set(student.class_id ?? -1, {
          class_id: student.class_id ?? -1,
          period_block: student.period_block ?? "Block",
          subject: student.subject ?? "Subject",
          totalFeedbackError: student.feedbackError ?? 0,
        });
      }
    });

    let maxClassData = Array.from(classDataMap.values()).reduce((max, cur) =>
      max.totalFeedbackError > cur.totalFeedbackError ? max : cur
    );

    return maxClassData;
  };

  useEffect(() => {
    if (!classList || !open || !targetId) return;
    let isCancelled = false;

    const fetchFeedbackData = async () => {
      if (!loadedDetailedData) {
        try {
          let data = await getClassesTargetFeedback(classList, targetId);
          if (!isCancelled) {
            // setGranularData(data);
            setHighestQuestion(findMaxFeedbackEntry(data));
            setHighestClass(findClassWithMaxError(data.hintReliance));
            setLoadedDetailedData(true);
          }
        } catch (err) {
          console.error("An unexpected error occurred:", err);
        }
      }
    };

    fetchFeedbackData();

    return () => {
      isCancelled = true;
    };
  }, [classList, loadedDetailedData, open, targetId]);

  return (
    <>
      <Modal open={open} onClose={handleClose} aria-labelledby={"student-detailed-modal"}>
        <Card className={classes.lessonPlanModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="insights-student-detail-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                {currentTarget?.getFormattedTargetName()}
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    HandleDataLayer(
                      "modal",
                      "close",
                      "click",
                      "main content",
                      currentTarget?.getFormattedTargetName()!,
                      "close cross",
                      "onclick"
                    );
                    props.handleClose();
                  }}
                  aria-label="close student detailed modal"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <CardContent
            sx={{
              padding: "8px 32px 32px 32px !important",
            }}>
            <Typography
              variant="h4"
              fontSize="20px"
              fontWeight="600"
              sx={{ marginBottom: "16px" }}>
              Skills
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: "16px" }}>
              We recommend strengthening your students’ knowledge in the following areas:
            </Typography>
            <List sx={{ marginBottom: "16px", padding: "0px" }}>
              {targetSkills?.slice(0, 3).map((skill, index) => {
                return (
                  <ListItem key={`${index}-${skill.id}`} sx={{ padding: "0px" }}>
                    <Typography variant="body1" component="div">
                      ∙ {skill.name}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>

            <Divider />
            <Typography
              variant="h4"
              fontSize="20px"
              fontWeight="600"
              sx={{ marginY: "16px" }}>
              Top Errors
            </Typography>
            <Grid container>
              <Grid item xs={6} sx={{ padding: "16px" }}>
                <Typography variant="h3" fontSize="24px" sx={{ marginBottom: "8px" }}>
                  {highestQuestion?.questionName}
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: "16px" }}>
                  Activity
                </Typography>
                <Button
                  onClick={() => {
                    HandleDataLayer(
                      "link",
                      "click",
                      "internal link",
                      "main content",
                      `${highestQuestion?.questionName}`,
                      "view activity",
                      "onclick",
                      "top errors"
                    );
                    handleClickActivity();
                  }}
                  className={classes.button}
                  size="small"
                  variant="outlined"
                  color="primary">
                  View Activity
                </Button>
              </Grid>
              <Grid item xs={6} sx={{ padding: "16px" }}>
                <Typography variant="h3" fontSize="24px" sx={{ marginBottom: "8px" }}>
                  {`${highestClass?.subject} ${highestClass?.period_block}`}
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: "16px" }}>
                  Class
                </Typography>
                <Button
                  onClick={() => {
                    HandleDataLayer(
                      "link",
                      "click",
                      "internal link",
                      "main content",
                      `${highestClass?.subject} ${highestClass?.period_block}`,
                      "view class",
                      "onclick",
                      "top errors"
                    );
                    handleClickClass();
                  }}
                  className={classes.button}
                  size="small"
                  variant="outlined"
                  color="primary">
                  View Class
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Modal>
    </>
  );
};
